import React from 'react';
import {
  SupportedLanguage,
  VocabularyLevel,
  MultipleChoiceQuestion,
  LANGUAGE_CONFIG,
  LEVEL_CONFIG
} from '../utils/multipleChoiceLogic';
import '../styles/MultipleChoiceGame.css';

interface GameStats {
  totalQuestions: number;
  correctAnswers: number;
  wrongAnswers: number;
  skippedCount: number;
  score: number;
  accuracy: number;
  timeTaken: number;
  wrongQuestions: MultipleChoiceQuestion[];
}

interface MultipleChoiceResultsProps {
  stats: GameStats;
  language: SupportedLanguage;
  level: VocabularyLevel;
  onPlayAgain: () => void;
  onExit: () => void;
}

const MultipleChoiceResults: React.FC<MultipleChoiceResultsProps> = ({
  stats,
  language,
  level,
  onPlayAgain,
  onExit
}) => {
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getGrade = (): { letter: string; color: string; message: string } => {
    if (stats.accuracy >= 90) return { letter: 'A', color: '#22c55e', message: 'Excellent!' };
    if (stats.accuracy >= 80) return { letter: 'B', color: '#84cc16', message: 'Great job!' };
    if (stats.accuracy >= 70) return { letter: 'C', color: '#eab308', message: 'Good effort!' };
    if (stats.accuracy >= 60) return { letter: 'D', color: '#f97316', message: 'Keep practicing!' };
    return { letter: 'F', color: '#ef4444', message: 'Need more practice' };
  };

  const grade = getGrade();

  return (
    <div className="results-container">
      <div className="results-header">
        <h2>Quiz Complete!</h2>
        <div className="language-level-info">
          <span>{LANGUAGE_CONFIG[language].flag}</span>
          <span>{LANGUAGE_CONFIG[language].name}</span>
          <span>•</span>
          <span>{LEVEL_CONFIG[level].name}</span>
        </div>
      </div>

      <div className="grade-circle" style={{ borderColor: grade.color }}>
        <span className="grade-letter" style={{ color: grade.color }}>{grade.letter}</span>
        <span className="grade-percent">{stats.accuracy}%</span>
      </div>
      <p className="grade-message" style={{ color: grade.color }}>{grade.message}</p>

      <div className="score-display-large">
        <span className="score-label">Final Score</span>
        <span className={`score-value ${stats.score >= 0 ? 'positive' : 'negative'}`}>
          {stats.score > 0 ? '+' : ''}{stats.score}
        </span>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-icon">✓</span>
          <span className="stat-number correct">{stats.correctAnswers}</span>
          <span className="stat-label">Correct</span>
        </div>
        <div className="stat-card">
          <span className="stat-icon">✗</span>
          <span className="stat-number wrong">{stats.wrongAnswers}</span>
          <span className="stat-label">Wrong</span>
        </div>
        <div className="stat-card">
          <span className="stat-icon">⏭</span>
          <span className="stat-number skipped">{stats.skippedCount}</span>
          <span className="stat-label">Skipped</span>
        </div>
        <div className="stat-card">
          <span className="stat-icon">⏱</span>
          <span className="stat-number time">{formatTime(stats.timeTaken)}</span>
          <span className="stat-label">Time</span>
        </div>
      </div>

      {stats.wrongQuestions.length > 0 && (
        <div className="wrong-questions-section">
          <h3>Review Wrong Answers ({stats.wrongQuestions.length})</h3>
          <div className="wrong-questions-list">
            {stats.wrongQuestions.slice(0, 5).map((q, index) => (
              <div key={index} className="wrong-question-item">
                <div className="wrong-question-prompt">{q.prompt}</div>
                <div className="wrong-question-answer">
                  <span className="answer-label">Correct:</span>
                  <span className="answer-value">{q.correctAnswer}</span>
                </div>
                {q.userWrongAnswers.length > 0 && (
                  <div className="wrong-question-attempts">
                    <span className="attempts-label">Your answers:</span>
                    <span className="attempts-value">{q.userWrongAnswers.join(', ')}</span>
                  </div>
                )}
              </div>
            ))}
            {stats.wrongQuestions.length > 5 && (
              <p className="more-wrong">
                +{stats.wrongQuestions.length - 5} more saved to your notebook
              </p>
            )}
          </div>
        </div>
      )}

      <div className="results-actions">
        <button className="play-again-button" onClick={onPlayAgain}>
          Play Again
        </button>
        <button className="exit-button-results" onClick={onExit}>
          Back to Menu
        </button>
      </div>
    </div>
  );
};

export default MultipleChoiceResults;
